import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import DataTable, { CellSize, DataTableProps } from "../table";
import { GetTitleRouterPath, UserHasAcl, UserHasLicence } from "../../../Utils/helpers";
import { assetService, cacheService } from "../../../Core/Api/services";
import { Typography, Link, Stack, Chip, Button } from "@mui/material";
import { Link as RouterLink } from "react-router";
import TableCellSingleCover from "../TableCellSingleCover/table-cell-single-cover";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import useCollectionActions from "../../../Utils/Hooks/useCollectionActions";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import ModalCreateShare from "../../Modals/ModalCreateShare/modal-create-share";
import { ModalContext } from "../../Modals";
import React from "react";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { GetMetadataByTagName, IsPermissionGranted } from "../../../Core/Utils/asset-object-utils";
import { produce } from "immer";
import AssetVideoResolutionChip from "../../Misc/Chips/AssetChip/asset-video-resolution-chip";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { PermissionType } from "../../../Core/Api/Types/permission-type";
import { Licences } from "../../../Core/Api/Types/licences";
import TableCellMovie from "../TableCellMovie/table-cell-movie";
import { useTranslation } from "react-i18next";
import TableUuids from "../table-uuids";

interface TitleTableProps extends Omit<DataTableProps, "columns" | "rows"> {
  titles: DbAssetObjectInfos[];
  setSelectedItems: React.Dispatch<React.SetStateAction<DbAssetObjectInfos[]>>;
  onPaginationModelUpdate?: (page: number, pageSize: number) => void;
  selectedItems: DbAssetObjectInfos[];
  extended: boolean;
  paginationModel?: {
    page: number;
    pageSize: number;
  };
  isLoading?: boolean;
}

const TitleTable: FC<TitleTableProps> = (props) => {
  const { t } = useTranslation();
  const {
    titles,
    setSelectedItems,
    onPaginationModelUpdate,
    paginationModel,
    selectedItems,
    extended,
    isLoading,
    ...otherProps
  } = props;

  const [titlesData, setTitlesData] = useState<DbAssetObjectInfos[]>([]);

  useEffect(() => {
    setTitlesData(titles);
  }, [titles]);

  // Modal context
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  // Hooks
  const { addObjectToBasket } = useBasketActions();
  const { AddItemsToCollection } = useCollectionActions();
  const apiGrid = useGridApiRef();

  // Acls
  const aclOrder = UserHasAcl(AclTypes.aclSectionOrderAccess);
  const aclShare = UserHasAcl(AclTypes.aclShareCreate);

  // Handlers
  const handleAddObjectToBasket = useCallback(
    (objectId: number) => {
      let obj = titles.find((x) => x.id === objectId);

      if (obj) addObjectToBasket(obj);
    },
    [titles]
  );

  const handleOpenNewShareModal = useCallback(
    (objectId: number) => {
      if (!openModal) return;
      openModal(ModalCreateShare, { object: titles.find((x) => x.id === objectId) });
    },
    [openModal, titles]
  );

  // Load title detailled graph if needed more data/columns
  useEffect(() => {
    if (!extended) return;
    titles.forEach((obj) => {
      assetService.GetGraph(obj.id, true, true, false).then(function (response) {
        if (response && response.graph && response.graph.metadata) {
          setTitlesData(
            produce((draft) => {
              let match = draft.find((x) => x.id === obj.id);

              if (match) {
                match.metadata?.push(response.graph!.metadata!.find((m) => m.fieldName === "allSubtitleLanguages")!);
                match.metadata?.push(response.graph!.metadata!.find((m) => m.fieldName === "allAudioLanguages")!);
                match.metadata?.push(response.graph!.metadata!.find((m) => m.fieldName === "allVideoQualities")!);
              }
            })
          );
        }
      });
    });
  }, [titles, extended]);

  const customerName = cacheService.GetCustomerExternalIdLabel();

  const cols: GridColDef[] = useMemo(
    () => [
      {
        field: "id",
        headerName: "Id",
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: any) => (
          <Link
            color="primary"
            underline="hover"
            component={RouterLink}
            target="_blank"
            to={`${GetTitleRouterPath()}/${data?.row?.id}`}
          >
            {data.value}
          </Link>
        ),
      },
      {
        field: "titleCustomerId",
        headerName: customerName + " Id",
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: any) => (
          <Typography component="span" fontWeight={500} color="text.primary">
            {data.value}
          </Typography>
        ),
      },
      //   {
      //     field: "",
      //     headerName: "Poster",
      //     minWidth: 80,
      //     flex: 80,
      //     renderCell: (data: any) => <TableCellSingleCover coverId={data.id} />,
      //   },
      //   {
      //     field: "name",
      //     headerName: "Title",
      //     minWidth: 120,
      //     flex: 120,
      //     renderCell: (data: any) => <Typography sx={{ my: 1 }}>{data.value}</Typography>,
      //   },
      {
        field: "name",
        headerName: t("common:title"),
        minWidth: CellSize.xlarge,
        flex: CellSize.xlarge,
        renderCell: (data: any) => <TableCellMovie id={data.id} name={data.value} />,
      },
      {
        field: "oar",
        headerName: "OAR",
        valueGetter: (data: any, row: any) => GetMetadataByTagName(row, "titleOriginalPictureFormat")?.value ?? "-",
        renderCell: (data: any) => (
          <Typography variant="body1" color="text.secondary">
            {data.value}
          </Typography>
        ),
        minWidth: CellSize.xsmall,
        flex: CellSize.xsmall,
      },
      {
        field: "allSubtitleLanguages",
        headerName: t("common:subtitleLanguages"),
        renderCell: (data: any) =>
          extended ? (
            <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
              {GetMetadataByTagName(data.row, "allSubtitleLanguages")?.value.map((x, index) => (
                <Chip label={x.label} key={index} size="small" />
              ))}
            </Stack>
          ) : (
            ""
          ),
        minWidth: CellSize.medium,
        flex: CellSize.medium,
      },
      {
        field: "allAudioLanguages",
        minWidth: CellSize.medium,
        flex: CellSize.medium,
        headerName: t("common:audioLanguagesLowerCase"),
        valueFormatter: (params: any, row: any) => {
          if (params) {
            return GetMetadataByTagName(row, "allAudioLanguages")
              ?.value.map((x) => x.label)
              .join(",");
          } else return "";
        },
        renderCell: (data: any) =>
          extended ? (
            <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
              {GetMetadataByTagName(data.row, "allAudioLanguages")?.value.map((x, index) => (
                <Chip label={x.label} key={index} size="small" />
              ))}
            </Stack>
          ) : (
            ""
          ),
      },
      {
        field: "allVideoQualities",
        headerName: t("table:headerName.qualities"),
        minWidth: CellSize.small,
        flex: CellSize.small,
        valueFormatter: (params: any, row: any) => {
          if (params) {
            return GetMetadataByTagName(row, "allVideoQualities")
              ?.value.map((x) => x)
              .join(",");
          } else return "";
        },
        renderCell: (data: any) =>
          extended ? (
            <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
              {GetMetadataByTagName(data.row, "allVideoQualities")?.value.map((x, index) => (
                <AssetVideoResolutionChip resolution={x} key={index} size="small" />
              ))}
            </Stack>
          ) : (
            ""
          ),
      },
      {
        field: "actions",
        headerName: "",
        type: "actions",
        minWidth: CellSize.actionsDefault,
        flex: CellSize.actionsDefault,
        align: "right",
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={<BookmarkBorderIcon sx={{ width: 20, height: 20, color: "text.secondary" }} />}
            label={t("button:addToCollection_one")}
            onClick={() =>
              AddItemsToCollection([new DbAssetObjectInfos({ id: params?.row?.id, name: params?.row?.title })])
            }
          />,
          <GridActionsCellItem
            icon={<ShareOutlinedIcon sx={{ width: 20, height: 20, color: "text.secondary" }} />}
            label={t("common:share")}
            onClick={() => handleOpenNewShareModal(params?.row?.id)}
            disabled={
              !aclShare ||
              !IsPermissionGranted(params.row, PermissionType.Share) ||
              !UserHasLicence(Licences.ShareScreening)
            }
          />,
          <GridActionsCellItem
            icon={<ShoppingBagOutlinedIcon sx={{ width: 20, height: 20, color: "primary.text" }} />}
            label={t("common:addToBasket")}
            onClick={() => handleAddObjectToBasket(params?.row?.id)}
            disabled={!aclOrder}
          />,
        ],
      },
    ],
    [addObjectToBasket, customerName, titlesData]
  );

  return (
    <DataTable
      loading={isLoading}
      tableKey={TableUuids.TITLES_TABLE}
      rows={titlesData}
      columns={cols}
      rowHeight={56}
      getRowHeight={() => "auto"}
      initialState={{ pinnedColumns: { left: ["__check__", "id", "name"], right: ["actions"] } }}
      checkboxSelection
      disableRowSelectionOnClick
      sx={{
        // pointer cursor on ALL rows
        "& .MuiDataGrid-row": {
          cursor: "default",
        },
      }}
      pagination={true}
      selectedRows={selectedItems.map((s) => s.id)}
      setSelectedRows={(e) => setSelectedItems(titles.filter((x) => (e as number[]).includes(x.id)))}
      // columnVisibilityModel={{ titleCustomerId: customerName !== "", allSubtitleLanguages: extended }}
      {...otherProps}
      apiRef={apiGrid}
      paginationServerSideChangeCallback={(page, pageSize) =>
        onPaginationModelUpdate && onPaginationModelUpdate(page, pageSize)
      }
      columnHeaderHeight={48}
      pageSize={paginationModel?.pageSize}
      paginationModel={paginationModel}
      paginationMode="server"
      densityControl
    />
  );
};

export default TitleTable;
